/* Delete an effort filed by mistake (designs/intake-delete.md). Admin-only,
   rendered by the drawer at the bottom of the record, apart from Edit and
   from the archive checkbox — archive keeps, delete removes (ID1), and the
   two must never be one click apart.

   The typed name and the reason are SENT, not only checked here: the server
   enforces both (ID3), so this dialog is a courtesy and not the guard. The
   server's refusals (linked, folded in, mapped, backed — ID2) are shown by
   name rather than second-guessed. */
import { useState, type JSX } from "react";

export function DeleteEffort({
  id,
  name,
  onDeleted,
}: {
  id: string;
  name: string;
  onDeleted: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameMatches = typed.trim() === name;
  const ready = nameMatches && reason.trim() !== "" && !busy;
  /* why the button is disabled, said out loud — a one-letter typo in the
     effort's name is otherwise a silent dead button. Quiet until there is
     something to correct. */
  const hint =
    typed.trim() !== "" && !nameMatches
      ? "Doesn't match the name yet — check the spelling, letter by letter."
      : nameMatches && reason.trim() === ""
        ? "Add a reason to enable delete."
        : "";

  const close = (): void => {
    setOpen(false);
    setTyped("");
    setReason("");
    setError(null);
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/intake/capabilities/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmName: typed.trim(), reason: reason.trim() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  if (!open)
    return (
      <div className="groom-actions" data-delete-effort>
        <button className="btn small danger" onClick={() => setOpen(true)}>
          Delete effort…
        </button>
        <span className="groom-hint">Admin · only for an effort filed by mistake</span>
      </div>
    );

  return (
    <div className="groom-actions" data-delete-effort>
      <div className="capture-grid">
        <p className="capture-wide">
          Deleting removes this effort, its grooming, comments and history permanently. It cannot
          be undone; only a receipt of who deleted it, when and why is kept. To hide it but keep
          the record, archive it instead (Edit → Archived).
        </p>
        <label className="capture-field capture-wide">
          <span>
            Type the effort's name to confirm: <code>{name}</code>
          </span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} disabled={busy} />
        </label>
        <label className="capture-field capture-wide">
          <span>Reason (kept on the receipt — do not repeat the content)</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
        </label>
      </div>
      <p className="groom-hint" data-delete-hint aria-live="polite">
        {hint}
      </p>
      {error !== null && <p className="capture-error">{error}</p>}
      <button className="btn small danger" disabled={!ready} onClick={() => void remove()}>
        {busy ? "Deleting…" : "Permanently delete"}
      </button>
      <button className="capture-notdup" onClick={close} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}
